import {
    Capacitor,
    registerPlugin,
    type PermissionState,
    type PluginListenerHandle,
} from "@capacitor/core"
import type { CapNotificationAllowedTags } from "./cap-notification-allowed-tags"
import { WebPlugin } from "@capacitor/core"
import { parseMockHTMLAST } from "./cap-mockhtml-parser"
import { writable } from "svelte/store"
import { addSeconds } from "date-fns"
import { isNative, platform } from "@utils/underhood/browser"

type CapNode = CapNodeText | CapNodeElement

/**
 * Needs to match type in [android channel
 * file](../../../android/app/src/main/java/org/circleofa/acimce/notifications/ACIMNotificationChannel.java)
 * as well as TODO ios file.
 */
export enum Channel {
    TEMPTATION = "response",
    FREQUENT_REMINDERS = "frequent",
    HOURLY_REMEMBRANCE = "hourly",
    MAIN_PRACTICE = "practice",
    LESSON_REMINDER = "lesson_reminder",
}

/**
 * All channels - careful not to accidentally deport
 */
export const ChannelsAll = [
    Channel.TEMPTATION,
    Channel.FREQUENT_REMINDERS,
    Channel.HOURLY_REMEMBRANCE,
    Channel.MAIN_PRACTICE,
    Channel.LESSON_REMINDER,
] as const

export enum ExactRepeating {
    HOUR = "hour",
    HALF_HOUR = "half",
    BOTH = "both",
}

export enum NotificationAtType {
    EXACT = "exact",
    REPEATING = "repeating",
    EXACT_REPEATING = "exact-repeating",
}

/**
 * Not all platforms support all things
 */
export const SUPPORTED_AT_TYPES: Set<NotificationAtType> = (() => {
    switch (platform) {
        case "android":
            return new Set([
                NotificationAtType.EXACT,
                NotificationAtType.EXACT_REPEATING,
                NotificationAtType.REPEATING,
            ])
        case "ios":
            return new Set([NotificationAtType.EXACT, NotificationAtType.EXACT_REPEATING])
        case "web":
        case "pwa":
            return new Set()
    }
})()

export type NotificationAtFrequency =
    | {
          frequency: NotificationAtType.REPEATING
          /**
           * Repeats every how many minutes, undefined or 0 for no-repeating
           */
          repeats: number
          on?: undefined
          date?: undefined
      }
    | {
          frequency: NotificationAtType.EXACT
          /**
           * Exact time
           */
          date: Date
          on?: undefined
          repeats?: undefined
      }
    | {
          frequency: NotificationAtType.EXACT_REPEATING
          /**
           * On certain minutes of the hour:
           * hour = on the hour every hour
           * half = on the half hour
           * both = on the hour and on the half hour
           */
          on: ExactRepeating
          date?: undefined
          repeats?: undefined
      }

interface CapNodeText {
    type: "text"
    text: string
}

/**
 * Contains html info. Android has built-in html parsers, but iOS doesn't,
 * so that means we need to pre-process it
 */
interface Html {
    html: string
    htmlAst: CapNode[]
}

interface CapNodeElement {
    type: "element"
    name: (typeof CapNotificationAllowedTags)[number]
    children: CapNode[]
}

export type CapNotificationType = {
    /**
     * title
     */
    title: string
    /**
     * body of the message
     */
    body: string

    html?: Html
    /**
     * Time & frequency, just to add on top so people can see it when they pull down
     */
    time?: string
    /**
     * **IMPORTANT!**
     *
     * In each lesson, this ID number needs to be unique!
     *
     * ID for this notification. This will be sent back when the user clicks on the notification
     * so we know where to direct it. In general, it's up to the ACIM engine to determine what we
     * want this to be. But generally, you would use the lesson number plus the ID to direct
     * the
     */
    id: number
    /**
     * Channel, good for icons and custom styling
     */
    channel: Channel
    at: NotificationAtFrequency
}

type RegisterNotificationOptions = {
    /**
     * Lesson number of the lesson setting the notifications. Note that for example
     * If you are on lesson 20 and set a reminder for lesson 21, it's still lesson 20's
     * notification. Lesson 20 "owns" the lesson 21 reminder
     */
    lesson: number
    /**
     * Time to stop repeating notifications, bed time
     */
    until: Date
    notifications: CapNotificationType[]
}

type RegisteredInfo = RegisterNotificationOptions

/**
 * Web version, unimplemented
 */
export class NotificationManagerWeb extends WebPlugin implements NotificationManagerPlugin {
    registerNotifications(options: RegisterNotificationOptions): Promise<void> {
        throw this.unimplemented("Not implemented on web.")
    }
    clearNotifications(): Promise<void> {
        throw this.unimplemented("Not implemented on web.")
    }
    checkPermissions(): Promise<PermissionsState> {
        throw this.unimplemented("Not implemented on web.")
    }

    /**
     * Not implemeneted in iOS
     */
    changeExactNotificationSetting(): Promise<ExactAlarmPermissionState> {
        throw this.unimplemented("Not implemented on web.")
    }

    /**
     * Not implemented in iOS
     */
    checkExactNotificationSetting(): Promise<ExactAlarmPermissionState> {
        throw this.unimplemented("Not implemented on web.")
    }

    getSetNotifications(): Promise<RegisteredInfo | undefined> {
        throw this.unimplemented("Not implemented on web.")
    }

    requestPermissions(): Promise<PermissionsState> {
        throw this.unimplemented("Not implemented on web.")
    }
}

type PermissionsState = { notifications: PermissionState }

type ExactAlarmPermissionState = { exact_alarm: PermissionState }

/**
 * Should be compatible with
 * [android channel
 * file](../../../android/app/src/main/java/org/circleofa/acimce/notifications/ACIMNotification.java)
 */
interface NotificationManagerPlugin {
    /**
     * Set all notifications
     *
     * Returns null on iOS, so setting it to void
     * @param options
     */
    registerNotifications(options: RegisterNotificationOptions): Promise<void>

    clearNotifications(): Promise<void>

    changeExactNotificationSetting(): Promise<ExactAlarmPermissionState>

    checkExactNotificationSetting(): Promise<ExactAlarmPermissionState>

    checkPermissions(): Promise<PermissionsState>

    requestPermissions(): Promise<PermissionsState>

    /**
     * {@link RegisteredInfo} if there's some set, and undefined if not.
     */
    getSetNotifications(): Promise<RegisteredInfo | undefined>

    /**
     * Listen for when an action is performed on a notification.
     */
    addListener(
        eventName: "timezone",
        listenerFunc: (data: Record<string, never>) => void,
    ): Promise<PluginListenerHandle>
    addListener(
        eventName: "received",
        listenerFunc: (data: { lesson: number; id: number }) => void,
    ): Promise<PluginListenerHandle>

    /**
     * Remove all listeners for this plugin.
     */
    removeAllListeners(): Promise<void>
}

export const NotificationManager = registerPlugin<NotificationManagerPlugin>(
    "NotificationManager",
    { web: Promise.resolve(new NotificationManagerWeb()) },
)

// // const exampleNotification = `
// // <p>This is <b>nested <i>bold and italic</i></b> text! We here have a long paragraph full of really cool things. <i><b>One thing</b> we can do is hang out</i>. But not for too long.</p>
// // <ul>
// //     <li>Item 1 is a really epic item. It holds the key to most things that are seemingly painful. <b>In essence</b>, it's not about what you think it is. But it also kinda is.</li>
// //     <li><b>Item 2</b> with bold</li>
// //     <li>Item 3 has <i>italic</i> text</li>
// // </ul>
// // <p>What about an ordered list?</p>
// // <ol>
// //     <li>Item 1 is a really epic item. It holds the key to most things that are seemingly painful. <b>In essence</b>, it's not about what you think it is. But it also kinda is.</li>
// //     <li><b>Item 2</b> with bold</li>
// //     <li>Item 3 has <i>italic</i> text</li>
// // </ol>
// // <p>Another paragraph with <i>italic</i> text.</p>
// // <p>Another paragraph with <i>italic</i> text and long information. For whom you ask? For anyone willing to ask I'd say</p>
// // `

// const exampleNotification = `<ul><li>Begin by repeating, “<i>I am not a body. I am free. For I am still as God created me</i>.”</li>
// <li>Then repeat today’s idea: “<i>I bless the world because I bless myself</i>.”</li></ul>

// <p>Perhaps also repeat the italicized lines that follow the idea (which are meant “to aid your practicing”—7:1).</p>

// <ul><li>For the bulk of the time, close your eyes and try to let go all beliefs you have about yourself and the world, all the concepts you think you know and understand. Realizing that these don’t reveal the truth so much as make your mind “deaf to reason, sanity, and simple truth” (3:8), consciously go through “a deep relinquishing” of them all (3:8). The aim is to reach a profound openness to what they have been shutting out: the direct experience of truth. After relinquishing your beliefs, simply wait for that experience in a wordless state of stillness and openness. Realize that this is also an openness to the Holy Spirit, Who may then come in and guide your meditation. He may, as the final lessons say, give you a word to help your practice, or a thought to focus on, or just “stillness and a tranquil, open mind” (W-361-365.1:3). Or He may take things in a completely unexpected direction.</li>
// <li>If a wandering thought intrudes—which will happen regularly—immediately respond with, “<i>This thought I do not want. I choose instead ‘I bless the world because I bless myself</i>.’” This is an extremely effective way of dispelling distracting thoughts.</li>
// <li>Close by again repeating, “<i>I am not a body. I am free. For I am still as God created me</i>.”</li></ul>`

// async function test() {
//     const rtn = await NotificationManager.registerNotifications({
//         lesson: 207,
//         until: new Date(),
//         notifications: [
//             {
//                 title: "Morning Practice",
//                 time: "at least 15 minutes",
//                 body: "Begin by repeating, “I am not a body. I am free. For I am still as God created me“",
//                 html: {
//                     html: exampleNotification,
//                     htmlAst: parseMockHTMLAST(exampleNotification),
//                 },
//                 id: 1,
//                 // repeats: 1
//                 channel: Channel.LESSON_REMINDER,
//                 at: {
//                     frequency: NotificationAtType.EXACT,
//                     date: addSeconds(new Date(), 10),
//                 },
//             },
//             // {
//             //     title: "Frequent Reminders",
//             //     body: "Begin by repeating, “I am not a body. I am free. For I am still as God created me“",
//             //     time: "just a moment",
//             //     html: {
//             //         html: exampleNotification,
//             //         htmlAst: parseMockHTMLAST(exampleNotification),
//             //     },
//             //     id: 2,
//             //     at: {
//             //         frequency: NotificationAtType.REPEATING,
//             //         repeats: 15,
//             //     },
//             //     channel: Channel.LESSON_REMINDER,
//             // },
//         ],
//     })
//     console.log(rtn)
// }

/**
 * List of notifications currently set into the native layer. Null if none is set.
 *
 * This is also init'd after the permissions are refreshed via {@link getSetNotifications}
 */
export const currentNativeNotifications = writable<RegisteredInfo | null>(null)

type Permissions = {
    notifications: PermissionState
    exact_alarm: PermissionState
}

/**
 * Checks the permissions of the current setup
 * @returns
 */
export async function checkPermissions(): Promise<Permissions> {
    if (!isNative) throw new Error("Trying to check permissions but not native")
    return {
        notifications: (await NotificationManager.checkPermissions()).notifications,
        exact_alarm: await checkExactNotificationSetting(),
    }
}

/**
 * Should not be used outside of this folder. Use higher level APIs instead
 *
 * Also, only does the notifications reminder, on android, we need to check
 * exact alarms as well
 */
export async function checkNotificationsPermissions(): Promise<PermissionState> {
    if (!isNative) throw new Error("Trying to check notifications permissions but not native")
    return (await NotificationManager.checkPermissions()).notifications
}

/**
 * Should not be used outside of this folder. Use higher level APIs instead
 *
 * Also, only does the notifications reminder, on android, we need to check
 * exact alarms as well
 */
export async function requestNotificationsPermissions(): Promise<PermissionState> {
    if (!isNative) throw new Error("Trying to request notifications permissions but not native")
    const result = await NotificationManager.requestPermissions()
    return result.notifications
}

export async function checkExactNotificationSetting(): Promise<PermissionState> {
    if (!isNative) throw new Error("Trying to check exact alarm permissions but not native")
    if (platform === "ios") return "granted"
    return (await NotificationManager.checkExactNotificationSetting()).exact_alarm
}

/**
 * Check if iOS before calling or else it will throw
 * @returns
 */
export async function changeExactNotificationSetting(): Promise<PermissionState> {
    if (!isNative) throw new Error("Trying to request exact alarm permissions but not native")
    if (platform === "ios") throw new Error("Trying to request exact alarm permissions on iOS")
    return (await NotificationManager.changeExactNotificationSetting()).exact_alarm
}

/**
 * Registers a set of notifications for a specific lesson.
 * @param options
 */
export async function registerNotifications(options: RegisterNotificationOptions) {
    if (!isNative) throw new Error("Trying to register notifications on web")
    await NotificationManager.registerNotifications(options)

    // Update store
    return await getSetNotifications()
}

/**
 * Gets the currently set notifications in the native device's memory
 *
 * Also updates the local store {@link currentNativeNotifications}
 */
export async function getSetNotifications() {
    if (!isNative) throw new Error("Getting notifications on web!")
    const rtn = (await NotificationManager.getSetNotifications()) ?? null
    if (rtn) {
        if (typeof rtn.until === "string") {
            rtn.until = new Date(rtn.until)
        }
        for (const notification of rtn.notifications) {
            if (typeof notification.at.date === "string") {
                // @ts-expect-error quick and dirty way to clean the bridging
                notification.at.date = new Date(notification.at.date)
            }
        }
    }
    currentNativeNotifications.set(rtn)
    return rtn
}

export async function clearAllNotifications() {
    if (!isNative) return
    await NotificationManager.clearNotifications()

    // Refresh store
    getSetNotifications()
}

// @ts-expect-error ***
globalThis.requestNotifications = () => NotificationManager.requestPermissions()
// // @ts-expect-error ***
// globalThis.testIt = () => test()
// @ts-expect-error ***
globalThis.clearNotifications = () => NotificationManager.clearNotifications()
// @ts-expect-error ***
globalThis.getSetNotifications = () => NotificationManager.getSetNotifications()

// @ts-expect-error ***
globalThis.checkExactNotificationSetting = checkExactNotificationSetting
// @ts-expect-error ***
globalThis.changeExactNotificationSetting = changeExactNotificationSetting
